import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import Nav from "../../../components/nav";
import * as devicesAPI from "../../../services/device";
import { generateDeviceName, generateSerial } from "../../../services/magicwords";
import { apiErrorMessage } from "../../../services/apiError";
import Stepper from "./Stepper";
import ProtocolStep from "./ProtocolStep";
import DeviceStep from "./DeviceStep";
import DetailsStep from "./DetailsStep";
import { CUSTOM, LORA, MQTT, buildPayload, isValid, stepsFor } from "./wizard";

const TITLES = { protocol: "Protocol", device: "Device", details: "Details", connect: "Connect" };

export default function AddDevicePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("protocol");
  const [state, setState] = useState(() => ({
    protocol: null, model: null, name: generateDeviceName(), serial: "", sourceId: "", profileId: "",
  }));
  const [modelEntry, setModelEntry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [simulator, setSimulator] = useState({ busy: false, error: null });

  const update = useCallback((patch) => setState((s) => ({ ...s, ...patch })), []);

  const modelLabel = modelEntry
    ? `${modelEntry.make} ${modelEntry.model}`
    : state.model === CUSTOM ? "Custom (CayenneLPP)" : null;

  const steps = stepsFor(state.protocol).map((key) => ({
    key,
    title: TITLES[key],
    subtitle: {
      protocol: state.protocol === MQTT ? "MQTT" : state.protocol === LORA ? "LoRaWAN" : "How it connects",
      device: modelLabel || "Which model",
      details: step === "details" || !state.name ? "Name and IDs" : state.name,
      connect: "First uplink",
    }[key],
  }));

  const selectProtocol = (protocol) => {
    setState((s) => (s.protocol === protocol ? s : {
      ...s,
      protocol,
      serial: protocol === MQTT ? generateSerial() : "",
    }));
    setError(null);
    setStep(protocol === MQTT ? "details" : "device");
  };

  const selectModel = (model, entry) => {
    update({ model });
    setModelEntry(entry);
    setStep("details");
  };

  const submit = async () => {
    if (!isValid(state)) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await devicesAPI.addDevice(buildPayload(state));
      navigate(`/devices/${created._id}`);
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to add device"));
      setSubmitting(false);
    }
  };

  const trySimulator = async () => {
    setSimulator({ busy: true, error: null });
    try {
      const created = await devicesAPI.addDevice({ name: generateDeviceName(), type: MQTT, serial: generateSerial() });
      navigate(`/devices/${created._id}?simulator=1`);
    } catch (err) {
      setSimulator({ busy: false, error: apiErrorMessage(err, "Failed to create a simulated device") });
    }
  };

  return (
    <div>
      <Nav />
      <main className="mx-auto max-w-7xl p-4 md:p-10">
        <Link to="/devices" className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" /> Devices
        </Link>
        <h1 className="mt-4 text-4xl font-semibold text-gray-900 dark:text-gray-50">Add a device</h1>
        <div className="mt-10 grid gap-10 md:grid-cols-[220px_1fr]">
          <Stepper steps={steps} current={step} />
          <div>
            {step === "protocol" && (
              <ProtocolStep
                protocol={state.protocol}
                onSelect={selectProtocol}
                onTrySimulator={trySimulator}
                simulatorBusy={simulator.busy}
                simulatorError={simulator.error}
              />
            )}
            {step === "device" && (
              <DeviceStep
                selected={state.model}
                onSelect={selectModel}
                onBack={() => setStep("protocol")}
              />
            )}
            {step === "details" && (
              <DetailsStep
                state={state}
                modelLabel={modelLabel}
                onChange={update}
                onChangeModel={() => setStep("device")}
                onBack={() => setStep(state.protocol === LORA ? "device" : "protocol")}
                onSubmit={submit}
                submitting={submitting}
                error={error}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
